import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { XMLParser } from "fast-xml-parser";
import {
  CheckCircle,
  FileText,
  Loader2,
  Save,
  Upload,
  XCircle,
} from "lucide-react";
import styled from "styled-components";

import { Button, Card, Message } from "./ImportComponentStyles";

const API_BASE = "https://server229256.nazwa.pl";
const MAX_XML_SIZE = 15 * 1024 * 1024;

const FinancialDropArea = styled.div`
  display: flex;
  min-height: 190px;
  padding: 28px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  border: 3px dashed ${(props) => (props.$dragging ? "#0b74e5" : "#1e90ff")};
  border-radius: 14px;
  background: ${(props) => (props.$dragging ? "#eef6ff" : "#fafbff")};
  box-shadow: ${(props) =>
    props.$dragging ? "0 8px 24px rgba(11, 116, 229, .18)" : "none"};
  color: #334155;
  text-align: center;
  cursor: pointer;
  transition: 0.2s ease;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px rgba(30, 144, 255, 0.25);
  }
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 12px;
  margin-top: 18px;
`;

const PreviewCard = styled.div`
  padding: 14px;
  border: 1px solid #dbe3ef;
  border-radius: 10px;
  background: #fff;

  small {
    display: block;
    margin-bottom: 5px;
    color: #64748b;
  }

  strong {
    color: #0f172a;
    overflow-wrap: anywhere;
  }
`;

const FileRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-top: 10px;
  padding: 12px;
  border: 1px solid ${(props) => (props.$error ? "#fecaca" : "#bbf7d0")};
  border-radius: 10px;
  background: ${(props) => (props.$error ? "#fff7f7" : "#f6fff8")};
`;

const ResultsTable = styled.div`
  margin-top: 24px;
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    white-space: nowrap;
  }

  th {
    color: #475569;
    font-size: 13px;
  }
`;

function scalar(value) {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(scalar).filter(Boolean).join(", ");
  if (typeof value === "object") return scalar(value["#text"]);
  return String(value).trim();
}

function amount(value) {
  const text = scalar(value).replace(/\s/g, "").replace(",", ".");
  return /^-?\d+(?:\.\d+)?$/.test(text) ? text : null;
}

function findStatementRoot(parsed) {
  const known = [
    "JednostkaInna",
    "JednostkaMala",
    "JednostkaMikro",
    "JednostkaOp",
  ];

  // Older files (including the supplied 2024 schema) start directly with
  // <JednostkaInna>, without Dokument/TrescDokumentu wrappers.
  for (const key of known) {
    if (parsed?.[key]?.Naglowek) {
      return { statement: parsed[key], unitType: key };
    }
  }

  // Newer e-Dokument files wrap the statement in Dokument/TrescDokumentu.
  const document = parsed?.Dokument || parsed?.document;
  const content = document?.TrescDokumentu || document?.trescDokumentu;
  if (!content || typeof content !== "object") {
    const directFallback = Object.entries(parsed || {}).find(
      ([, value]) => value && typeof value === "object" && value.Naglowek
    );
    return directFallback
      ? { statement: directFallback[1], unitType: directFallback[0] }
      : null;
  }

  for (const key of known) {
    if (content[key]) return { statement: content[key], unitType: key };
  }

  const fallbackKey = Object.keys(content).find(
    (key) => content[key] && typeof content[key] === "object"
  );
  return fallbackKey
    ? { statement: content[fallbackKey], unitType: fallbackKey }
    : null;
}

function collectFinancialRows(node, section, path = [], rows = []) {
  if (!node || typeof node !== "object") return rows;

  if (Array.isArray(node)) {
    node.forEach((item, index) =>
      collectFinancialRows(item, section, [...path, String(index + 1)], rows)
    );
    return rows;
  }

  const amountA = amount(node.KwotaA);
  const amountB = amount(node.KwotaB);
  if (amountA !== null || amountB !== null) {
    rows.push({
      section,
      code: path[path.length - 1] || section,
      path: path.join("."),
      amountA,
      amountB,
    });
  }

  Object.entries(node).forEach(([key, child]) => {
    if (key === "KwotaA" || key === "KwotaB" || key.startsWith("@_")) return;
    if (child && typeof child === "object") {
      collectFinancialRows(child, section, [...path, key], rows);
    }
  });

  return rows;
}

function findAttachments(node, results = []) {
  if (!node || typeof node !== "object") return results;
  if (Array.isArray(node)) {
    node.forEach((item) => findAttachments(item, results));
    return results;
  }

  if (node.Nazwa && node.Zawartosc) {
    const encoded = scalar(node.Zawartosc).replace(/\s/g, "");
    results.push({
      name: scalar(node.Nazwa),
      estimatedSizeBytes: Math.floor((encoded.length * 3) / 4),
    });
  }

  Object.values(node).forEach((child) => {
    if (child && typeof child === "object") findAttachments(child, results);
  });
  return results;
}

async function sha256(text) {
  const bytes = new TextEncoder().encode(text);
  const digest = await window.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

async function parseFinancialStatement(file) {
  const xml = await file.text();
  if (!xml.trim().startsWith("<")) throw new Error("Plik nie zawiera XML.");

  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: "@_",
    removeNSPrefix: true,
    parseTagValue: false,
    trimValues: true,
  });
  const parsed = parser.parse(xml);
  const found = findStatementRoot(parsed);
  if (!found?.statement?.Naglowek) {
    throw new Error("Nie znaleziono sprawozdania finansowego ani jego nagłówka.");
  }

  const { statement, unitType } = found;
  const header = statement.Naglowek || {};
  const intro = statement.WprowadzenieDoSprawozdaniaFinansowego || {};
  const company = intro?.P_1?.P_1A || {};
  // 2025 e-Dokument: P_1A/Siedziba/AdresPol.
  // 2024 standalone schema: P_1B/Adres.
  const address =
    company?.Siedziba?.AdresPol ||
    intro?.P_1?.P_1B?.Adres ||
    company?.Siedziba ||
    {};
  const statementCode = header.KodSprawozdania || {};
  const pkdValues =
    intro?.P_1?.P_1C?.KodPKD2007 ?? intro?.P_1?.P_1C?.KodPKD ?? [];

  const financialRows = [];
  [
    ["Bilans", statement.Bilans],
    ["RZiS", statement.RZiS],
    ["ZestawienieZmianWKapitale", statement.ZestawienieZmianWKapitale],
    ["RachunekPrzeplywowPienieznych", statement.RachunekPrzeplywowPienieznych],
  ].forEach(([section, value]) => {
    if (value) collectFinancialRows(value, section, [], financialRows);
  });

  if (!financialRows.length) {
    throw new Error("Nie znaleziono pozycji finansowych KwotaA/KwotaB.");
  }

  const sections = [...new Set(financialRows.map((row) => row.section))];
  const attachments = findAttachments(
    statement.DodatkoweInformacjeIObjasnieniaJednostkaInna ||
      statement.DodatkoweInformacjeIObjasnienia
  );

  const payload = {
    sourceHash: await sha256(xml),
    metadata: {
      unitType,
      statementName: scalar(statementCode),
      systemCode: scalar(statementCode?.["@_kodSystemowy"]),
      schemaVersion: scalar(statementCode?.["@_wersjaSchemy"]),
      variant: scalar(header.WariantSprawozdania),
      periodFrom: scalar(header.OkresOd),
      periodTo: scalar(header.OkresDo),
      preparedAt: scalar(header.DataSporzadzenia),
      companyName: scalar(company.NazwaFirmy),
      nip: scalar(intro?.P_1?.P_1D),
      krs: scalar(intro?.P_1?.P_1E),
      pkdCodes: Array.isArray(pkdValues)
        ? pkdValues.map(scalar).filter(Boolean)
        : [scalar(pkdValues)].filter(Boolean),
      address: {
        country: scalar(address.KodKraju),
        province: scalar(address.Wojewodztwo),
        county: scalar(address.Powiat),
        municipality: scalar(address.Gmina),
        street: scalar(address.Ulica),
        houseNumber: scalar(address.NrDomu),
        apartmentNumber: scalar(address.NrLokalu),
        city: scalar(address.Miejscowosc),
        postalCode: scalar(address.KodPocztowy),
      },
    },
    introduction: {
      reportingPeriodFrom: scalar(intro?.P_3?.DataOd),
      reportingPeriodTo: scalar(intro?.P_3?.DataDo),
      accountingPolicy: scalar(intro?.P_7?.P_7A),
    },
    summary: {
      financialRowCount: financialRows.length,
      sections,
      attachmentCount: attachments.length,
    },
    financialRows,
    attachments,
  };

  if (!payload.metadata.periodFrom || !payload.metadata.periodTo) {
    throw new Error("Brak dat okresu sprawozdawczego.");
  }
  if (!payload.metadata.nip) throw new Error("Brak NIP w sprawozdaniu.");

  return payload;
}

function authHeaders() {
  return { Authorization: localStorage.getItem("token") };
}

export default function FinancialStatementImport() {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedStatements, setSavedStatements] = useState([]);

  const loadSavedStatements = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/financial-statements`, {
        headers: authHeaders(),
      });
      setSavedStatements(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Cannot load financial statements:", error);
    }
  };

  useEffect(() => {
    loadSavedStatements();
  }, []);

  const analyseFiles = async (selectedFiles) => {
    const next = [];
    setMessage(null);

    for (const file of selectedFiles) {
      if (!file.name.toLowerCase().endsWith(".xml")) {
        next.push({ fileName: file.name, status: "error", error: "Dozwolone są tylko pliki .xml." });
        continue;
      }
      if (file.size > MAX_XML_SIZE) {
        next.push({ fileName: file.name, status: "error", error: "Plik przekracza limit 15 MB." });
        continue;
      }

      try {
        const statement = await parseFinancialStatement(file);
        next.push({ fileName: file.name, status: "ready", statement });
      } catch (error) {
        next.push({ fileName: file.name, status: "error", error: error.message });
      }
    }

    setFiles(next);
    const readyCount = next.filter((item) => item.status === "ready").length;
    setMessage({
      isError: readyCount === 0,
      text: readyCount
        ? `Przeanalizowano ${readyCount} plik(i). Sprawdź podgląd i zapisz dane.`
        : "Nie znaleziono prawidłowego sprawozdania finansowego.",
    });
  };

  const onInput = async (event) => {
    await analyseFiles(Array.from(event.target.files || []));
    event.target.value = "";
  };

  const saveReadyFiles = async () => {
    const ready = files.filter((item) => item.status === "ready");
    if (!ready.length) {
      setMessage({ isError: true, text: "Brak prawidłowych danych do zapisania." });
      return;
    }

    setSaving(true);
    let saved = 0;
    const updated = [...files];

    for (const item of ready) {
      const index = updated.findIndex((entry) => entry === item);
      try {
        await axios.post(
          `${API_BASE}/api/financial-statements`,
          { fileName: item.fileName, statement: item.statement },
          { headers: authHeaders() }
        );
        updated[index] = { ...item, status: "saved" };
        saved += 1;
      } catch (error) {
        updated[index] = {
          ...item,
          status: "error",
          error: error?.response?.data?.error || error.message,
        };
      }
      setFiles([...updated]);
    }

    setSaving(false);
    setMessage({
      isError: saved !== ready.length,
      text:
        saved === ready.length
          ? `Zapisano ${saved} sprawozdanie(a) w bazie danych.`
          : `Zapisano ${saved} z ${ready.length} sprawozdań. Sprawdź błędy przy plikach.`,
    });
    await loadSavedStatements();
  };

  const firstReady = files.find((item) => item.status === "ready");
  const preview = firstReady?.statement;

  return (
    <Card>
      <h2>Import sprawozdania finansowego XML</h2>
      <p style={{ color: "#64748b", marginTop: 8 }}>
        System rozpoznaje strukturę sprawozdania, wyciąga dane firmy, okres oraz
        pozycje finansowe. Podpis i zawartość załączonych plików Base64 nie są
        zapisywane w bazie.
      </p>

      <FinancialDropArea
        $dragging={dragging}
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") inputRef.current?.click();
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          analyseFiles(Array.from(event.dataTransfer.files || []));
        }}
      >
        <Upload size={52} color="#1e90ff" />
        <strong>Przeciągnij sprawozdanie XML tutaj</strong>
        <span>lub kliknij, aby wybrać plik (maks. 15 MB)</span>
      </FinancialDropArea>
      <input ref={inputRef} type="file" accept=".xml,text/xml,application/xml" multiple hidden onChange={onInput} />

      {message && <Message $isError={message.isError}>{message.text}</Message>}

      {files.map((item) => (
        <FileRow key={`${item.fileName}-${item.statement?.sourceHash || item.error}`} $error={item.status === "error"}>
          <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <FileText size={18} />
            <span>
              <strong>{item.fileName}</strong>
              {item.error && <small style={{ display: "block", color: "#b91c1c" }}>{item.error}</small>}
            </span>
          </span>
          {item.status === "error" ? (
            <XCircle size={19} color="#b91c1c" />
          ) : (
            <CheckCircle size={19} color="#15803d" />
          )}
        </FileRow>
      ))}

      {preview && (
        <PreviewGrid>
          <PreviewCard><small>Firma</small><strong>{preview.metadata.companyName || "-"}</strong></PreviewCard>
          <PreviewCard><small>NIP</small><strong>{preview.metadata.nip}</strong></PreviewCard>
          <PreviewCard><small>Okres</small><strong>{preview.metadata.periodFrom} – {preview.metadata.periodTo}</strong></PreviewCard>
          <PreviewCard><small>Typ</small><strong>{preview.metadata.statementName || preview.metadata.unitType}</strong></PreviewCard>
          <PreviewCard><small>Pozycje finansowe</small><strong>{preview.summary.financialRowCount}</strong></PreviewCard>
          <PreviewCard><small>Sekcje</small><strong>{preview.summary.sections.join(", ")}</strong></PreviewCard>
        </PreviewGrid>
      )}

      {files.some((item) => item.status === "ready") && (
        <Button
          onClick={saveReadyFiles}
          disabled={saving}
          style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 20 }}
        >
          {saving ? <Loader2 size={19} className="spin" /> : <Save size={19} />}
          {saving ? "Zapisywanie..." : "Zapisz sprawozdanie w bazie"}
        </Button>
      )}

      {savedStatements.length > 0 && (
        <ResultsTable>
          <h3>Zaimportowane sprawozdania</h3>
          <table>
            <thead>
              <tr><th>Plik</th><th>Firma</th><th>NIP</th><th>Okres</th><th>Pozycje</th><th>Import</th></tr>
            </thead>
            <tbody>
              {savedStatements.map((row) => (
                <tr key={row._id}>
                  <td>{row.fileName}</td>
                  <td>{row.metadata?.companyName || "-"}</td>
                  <td>{row.metadata?.nip || "-"}</td>
                  <td>{row.metadata?.periodFrom || "-"} – {row.metadata?.periodTo || "-"}</td>
                  <td>{row.summary?.financialRowCount || 0}</td>
                  <td>{row.importedAt ? new Date(row.importedAt).toLocaleString("pl-PL") : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ResultsTable>
      )}
    </Card>
  );
}
